import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import useMediaQuery from '../../hooks/useMediaQuery';
import logo from '../../assets/maxelpay-logo-dark.svg';
import './onboarding.css';

/*
 * Preparing — the handoff between the wizard and the dashboard.
 *
 * Reached two ways: finishing step 3, and "Save & exit" from any step. Renders
 * full-bleed (registered outside OnboardingLayout) so the rail is gone and the
 * transition reads as leaving setup rather than another step.
 *
 * The three lines describe work that has actually happened: the step mutations
 * saved the details and the plan, and the dashboard really is loading. Nothing
 * here is invented progress theatre.
 */

const TASKS = [
  'Saving your details',
  'Applying your plan',
  'Loading your dashboard',
];

// Slow enough to read, short enough not to be in the way.
const TICK_MS = 520;
const EXIT_DELAY_MS = 520;

const Preparing = () => {
  const navigate = useNavigate();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Reduced motion gets the finished state immediately: someone who opted out
  // of animation should not be made to sit through a staged reveal.
  const [doneCount, setDoneCount] = useState(reduceMotion ? TASKS.length : 0);

  useEffect(() => {
    if (reduceMotion) {
      const t = setTimeout(() => navigate('/dashboard', { replace: true }), 400);
      return () => clearTimeout(t);
    }

    const timers = TASKS.map((_, i) =>
      setTimeout(() => setDoneCount(i + 1), TICK_MS * (i + 1))
    );
    // replace: true so Back does not drop the merchant into a finished wizard.
    timers.push(
      setTimeout(
        () => navigate('/dashboard', { replace: true }),
        TICK_MS * TASKS.length + EXIT_DELAY_MS
      )
    );

    return () => timers.forEach(clearTimeout);
  }, [navigate, reduceMotion]);

  return (
    <div className="ob-prep">
      <div className="ob-prep-inner" role="status" aria-live="polite">
        <span className="ob-prep-logo">
          <img src={logo} alt="MaxelPay" />
        </span>

        <span className="ob-prep-ring" aria-hidden="true">
          <span className="ob-prep-arc" />
          <span className="ob-prep-core" />
        </span>

        <h1 className="ob-prep-title">Preparing your dashboard</h1>
        <p className="ob-prep-sub">This only takes a moment.</p>

        <ul className="ob-prep-tasks">
          {TASKS.map((task, i) => {
            const isDone = i < doneCount;
            const isActive = i === doneCount;
            return (
              <li
                key={task}
                className={`ob-prep-task${isDone ? ' is-done' : ''}${isActive ? ' is-active' : ''}`}
              >
                <span className="ob-prep-tick" aria-hidden="true">
                  {isDone && <Check size={12} strokeWidth={3.2} />}
                </span>
                {task}
              </li>
            );
          })}
        </ul>

        <span className="ob-prep-bar" aria-hidden="true"><i /></span>
      </div>
    </div>
  );
};

export default Preparing;
